/**
 * @file SharePoint integration composable.
 *
 * Owns the singleton `SharePointClient`, exposes it reactively via
 * `useSharePoint()`, and wires the higher-level handlers (group requests,
 * collection / config replication) against it.
 */

import { SharePointClient } from '../../sharepoint/client.js';
import { isLocalhost } from '../../sharepoint/utils.js';
import {
  providers,
  createDirectGroupHandler,
} from '../../user/composables/user.js';
import { replicateWithSharePoint } from '../../sharepoint-replication/index.js';
import { buildGroupRequests } from './groupRequests.js';

const client = Vue.ref(null);
const groupMapping = Vue.ref({});
const configRootPath = Vue.ref('');

let _initializeResolve;
const _initializePromise = new Promise(resolve => {
  _initializeResolve = resolve;
});
const ensureInitialized = () => _initializePromise;

const useLocalHandlers = isLocalhost();

async function discoverSharePointLists() {
  await ensureInitialized();
  const response = await client.value.request(
    'web/lists?$select=Id,Title,ListItemEntityTypeFullName',
    'GET'
  );
  return response?.d?.results || [];
}

async function replicateCollection(options = {}) {
  await ensureInitialized();
  options.spClient = client.value;
  return replicateWithSharePoint(options);
}

function createSetupReplication(options = {}) {
  return collection => replicateCollection({ collection, ...options });
}

/**
 * Build a setupReplication handler for a config — pulls/pushes the config
 * value as a `.conf` file in the SharePoint configRootPath folder.
 *
 * @param {object} [options]
 * @param {string} [options.filename] - Defaults to `${configName}.conf`.
 * @param {number} [options.pullInterval]
 * @param {number} [options.pushDebounce=1000]
 * @param {string} [options.customRootPath] - Overrides `configRootPath`.
 * @returns {(configName: string) => Promise<object>}
 */
function replicateConfig(options = {}) {
  const {
    filename: customFilename,
    pullInterval,
    pushDebounce = 1000,
    customRootPath,
  } = options;

  return async configName => {
    await ensureInitialized();

    const filename = customFilename || `${configName}.conf`;
    const rootPath = customRootPath ?? configRootPath.value;
    const files = client.value.files({ rootPath });

    const pull = async currentCheckpoint => {
      try {
        const currentETag = currentCheckpoint?.etag;
        const serverETag = await files.getETag(filename);
        if (!serverETag) return null;
        if (serverETag === currentETag) return null;

        const { data } = await files.downloadWithETag(filename);
        let serverValue;
        try {
          serverValue = JSON.parse(data);
        } catch (parseError) {
          console.error(
            `Failed to parse config '${configName}' from SharePoint:`,
            parseError
          );
          return null;
        }
        return {
          data: serverValue,
          checkpoint: { etag: serverETag, lastSync: Date.now(), filename },
        };
      } catch (error) {
        if (error.message.includes('404')) return null;
        console.error(
          `Failed to pull config '${configName}' from SharePoint:`,
          error
        );
        throw error;
      }
    };

    const push = async (value, currentCheckpoint) => {
      const jsonData = JSON.stringify(value, null, 2);
      await files.uploadFile(filename, jsonData);
      return {
        etag: currentCheckpoint?.etag,
        lastSync: Date.now(),
        filename,
      };
    };

    return { pull, push, pullInterval, pushDebounce };
  };
}

function getFileManager(rootPath) {
  return client.value.files({ rootPath });
}

const groupRequests = buildGroupRequests({
  client,
  groupMapping,
  ensureInitialized,
  replicateCollection,
});

const sharePoint = Vue.reactive({
  client,
  discoverSharePointLists,
  getFileManager,
  ensureInitialized,
  replicateCollection,
  getCurrentUser: useLocalHandlers
    ? providers.guest.getCurrentUser
    : groupRequests.getCurrentUser,
  createGroupRequestHandler: useLocalHandlers
    ? createDirectGroupHandler
    : groupRequests.createGroupRequestHandler,
  createNativeGroupRequestHandler: useLocalHandlers
    ? createDirectGroupHandler
    : groupRequests.createNativeGroupRequestHandler,
  createSetupReplication: useLocalHandlers
    ? () => null
    : createSetupReplication,
  replicateConfig: useLocalHandlers ? () => null : replicateConfig,
});

/**
 * Initialize the SharePoint integration. Call once at app startup.
 *
 * @param {object} options - Forwarded to `SharePointClient`, plus:
 * @param {object} [options.groupMapping={}] - app-group → SP-group-id map.
 * @param {string} [options.configRootPath=''] - SP folder for config files.
 */
export function initSharePoint(options) {
  const opts = { groupMapping: {}, configRootPath: '', ...options };
  groupMapping.value = opts.groupMapping;
  configRootPath.value = opts.configRootPath;
  client.value = new SharePointClient(opts);
  _initializeResolve();
}

/**
 * @returns {object} The reactive SharePoint integration object.
 */
export function useSharePoint() {
  return sharePoint;
}
